/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IActionContext, getUrlSync } from '@msdyn365-commerce/core';
import { ICheckoutState } from '@msdyn365-commerce/global-state';
import { SimpleProduct, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { CartCheckout, EmptyActiveCart } from '@msdyn365-commerce-modules/checkout';
import { EmptyActiveCartInvoice } from './empty-active-cart-invoice';

export default async (
    ctx: IActionContext,
    checkoutState: ICheckoutState | undefined,
    orderedProducts: SimpleProduct[] | undefined,
    redirect: boolean,
    updatedCartVersion?: number,
    isPaymentVerificationRedirection?: boolean
) => {
    return PlaceOrder(ctx, checkoutState, orderedProducts, redirect, updatedCartVersion, isPaymentVerificationRedirection);
};

/**
 * Known action error failure states
 */
const enum ActionErrorStatus {
    Timeout = 'Timeout',
    HookTimeout = 'Hook Timeout',
    ServiceUnavailable = 'Service Unavailable'
}

const toError = (error: unknown): Error => {
    return error instanceof Error ? error : new Error('Unknown checkout error');
};

export const PlaceOrder = async (
    ctx: IActionContext,
    checkoutState: ICheckoutState | undefined,
    orderedProducts: SimpleProduct[] | undefined,
    redirect: boolean,
    updatedCartVersion?: number,
    isPaymentVerificationRedirection?: boolean
) => {
    return new Promise<void>(async (resolve: () => void, reject: (reason?: Error) => void) => {
        ctx.telemetry.information('Checkout place-order method is triggered.');

        let salesOrder: SalesOrder;

        // Proceed checkout
        try {
            salesOrder = await CartCheckout(ctx, updatedCartVersion, isPaymentVerificationRedirection);
        } catch (error) {
            const errorWithStatus = error as { status?: string };
            const err = error instanceof Error ? error : new Error('Unknown error');
            const status = (error as { status?: string })?.status;
            ctx.telemetry.exception(toError(error));
            ctx.telemetry.error('new-place-order failed', { name: err.name, message: err.message, stack: err.stack, status });
            if (errorWithStatus.status === ActionErrorStatus.Timeout || errorWithStatus.status === ActionErrorStatus.ServiceUnavailable) {
                ctx.telemetry.error(
                    'new-place-order Checkout emptyActiveCartInvoice is called - failed due to timeout or service unavailable'
                );
                await EmptyActiveCartInvoice(ctx);
            }
            reject(toError(error));
            return;
        }

        // Remove purchased items from the active cart
        try {
            ctx.telemetry.error('new-place-order Checkout emptyActiveCart is called.');
            await EmptyActiveCart(ctx);
        } catch (error) {
            ctx.telemetry.exception(toError(error));
            ctx.telemetry.error('new-place-order Checkout emptyActiveCart failed.');
            reject(toError(error));
            return;
        }

        if (redirect) {
            ctx.telemetry.error('new-place-order Redirect to order confirmation page.');

            // Redirect to the order confirmation page
            const orderConfirmationUrl = getUrlSync('orderConfirmation', ctx) || '';
            if (!orderConfirmationUrl) {
                reject(new Error('Error: No orderConfirmationUrl'));
                return;
            }

            const separator = orderConfirmationUrl.includes('?') ? '&' : '?';
            const url = `${orderConfirmationUrl}${separator}transactionId=${salesOrder.Id}`;
            window.location.assign(url);
        } else {
            ctx.telemetry.error('new-place-order Not redirect to order confirmation page.');

            if (checkoutState) {
                const result = await checkoutState.updateSalesOrder({
                    newSalesOrder: salesOrder,
                    newOrderedProducts: orderedProducts || []
                });

                if (result.status === 'FAILED') {
                    reject(new Error('Error: Updating error state failed'));
                    return;
                }
            } else {
                reject(new Error('Error: No checkout state'));
                return;
            }
            resolve();
        }
    });
};
